import React, { useState } from "react";

import type {
  ReviewItem,
  ReviewListAction,
  ReviewPanelItem,
} from "./ReviewPanel";

export type ChildMeta = {
  id: string;
  benefitKey: string;
  identifier: string;
  comment: string;
};

type SelectionEntry = {
  benefitKey: string;
  identifier: string;
  comment: string;
};

type ReviewSelectionResult = {
  checkedMap: Map<string, SelectionEntry>;
  allItemsMeta: ChildMeta[];
  isChildChecked: (id: string) => boolean;
  getChildMetas: (item: ReviewPanelItem) => ChildMeta[];
  parentState: (item: ReviewPanelItem) => {
    childMetas: ChildMeta[];
    parentChecked: boolean;
    parentIndeterminate: boolean;
  };
  handleChildToggle: (
    selectionId: string,
    benefitKey: string,
    identifier: string,
    comment: string | null | undefined,
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleGroupToggle: (
    items: ChildMeta[],
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleTopToggle: (
    items: ChildMeta[],
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  removeFromCheckedMap: (id: string) => void;
  handleDeleteSelected: () => void;
};

export const useReviewSelection = (
  data: ReviewPanelItem[],
  onUpdate: (action: ReviewListAction) => void,
): ReviewSelectionResult => {
  const [checkedMap, setCheckedMap] = useState<Map<string, SelectionEntry>>(
    new Map(),
  );

  const isChildChecked = (id: string): boolean => checkedMap.has(id);

  const getChildMetas = (item: ReviewPanelItem): ChildMeta[] =>
    (item.reviewItems ?? []).map((reviewItem: ReviewItem) => ({
      id: `${item.benefitKey}-${reviewItem.cellIdentifier}`,
      benefitKey: item.benefitKey,
      identifier: reviewItem.identifier,
      comment: reviewItem.comment ?? "",
    }));

  const parentState = (
    item: ReviewPanelItem,
  ): {
    childMetas: ChildMeta[];
    parentChecked: boolean;
    parentIndeterminate: boolean;
  } => {
    const childMetas = getChildMetas(item);
    const total = childMetas.length;
    const selectedCount = childMetas.filter((meta) =>
      checkedMap.has(meta.id),
    ).length;
    return {
      childMetas,
      parentChecked: total > 0 && selectedCount === total,
      parentIndeterminate: selectedCount > 0 && selectedCount < total,
    };
  };

  const handleChildToggle =
    (
      selectionId: string,
      benefitKey: string,
      identifier: string,
      comment: string | null | undefined,
    ) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.stopPropagation();
      setCheckedMap((prev) => {
        const next = new Map(prev);
        if (next.has(selectionId)) next.delete(selectionId);
        else
          next.set(selectionId, {
            benefitKey,
            identifier,
            comment: comment ?? "",
          });
        return next;
      });
    };

  const handleGroupToggle =
    (items: ChildMeta[]) => (event: React.ChangeEvent<HTMLInputElement>) => {
      event.stopPropagation();
      const allChecked =
        items.length > 0 && items.every((item) => checkedMap.has(item.id));
      const shouldCheck = !allChecked;
      setCheckedMap((prev) => {
        const next = new Map(prev);
        items.forEach((item) => {
          if (shouldCheck)
            next.set(item.id, {
              benefitKey: item.benefitKey,
              identifier: item.identifier,
              comment: item.comment ?? "",
            });
          else next.delete(item.id);
        });
        return next;
      });
    };

  const handleTopToggle =
    (items: ChildMeta[]) => (event: React.ChangeEvent<HTMLInputElement>) => {
      event.stopPropagation();
      const allChecked =
        items.length > 0 && items.every((item) => checkedMap.has(item.id));
      const shouldCheck = !allChecked;
      setCheckedMap((prev) => {
        const next = new Map(prev);
        if (shouldCheck)
          items.forEach((item) =>
            next.set(item.id, {
              benefitKey: item.benefitKey,
              identifier: item.identifier,
              comment: item.comment ?? "",
            }),
          );
        else items.forEach((item) => next.delete(item.id));
        return next;
      });
    };

  const removeFromCheckedMap = (id: string): void => {
    setCheckedMap((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  };

  const handleDeleteSelected = (): void => {
    if (checkedMap.size === 0) return;

    checkedMap.forEach((entry) => {
      const benefit = data.find((item) => item.benefitKey === entry.benefitKey);
      const reviewItem = benefit?.reviewItems.find(
        (ri) => ri.identifier === entry.identifier,
      );
      if (reviewItem) {
        onUpdate({
          type: "remove",
          payload: {
            fieldCommentId: reviewItem.fieldCommentId,
            planId: reviewItem.planId,
          },
        });
      }
    });

    setCheckedMap(new Map());
  };

  const allItemsMeta: ChildMeta[] = data.flatMap((item) =>
    (item.reviewItems ?? []).map((reviewItem: ReviewItem) => ({
      id: `${item.benefitKey}-${reviewItem.cellIdentifier}`,
      benefitKey: item.benefitKey,
      identifier: reviewItem.identifier,
      comment: reviewItem.comment ?? "",
    })),
  );

  return {
    checkedMap,
    allItemsMeta,
    isChildChecked,
    getChildMetas,
    parentState,
    handleChildToggle,
    handleGroupToggle,
    handleTopToggle,
    removeFromCheckedMap,
    handleDeleteSelected,
  };
};
